import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Mic, MicOff, Loader2 } from 'lucide-react';

/** Dialog input vocale: ascolto, testo modificabile, parsing e conferma. Stato nel parent. */
export default function VoiceDialog({
  open, isListening, voiceText, setVoiceText, voiceParsing, voiceParsedData, setVoiceParsedData,
  onStart, onStop, onParse, onConfirm, onCancel,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="font-black flex items-center gap-2">
            <Mic className="h-5 w-5 text-cyan-500" />
            Appuntamento Vocale
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isListening && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-cyan-500 flex items-center justify-center animate-pulse">
                <Mic className="h-8 w-8 text-white" />
              </div>
              <p className="text-lg font-medium text-cyan-600">Sto ascoltando...</p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-mono uppercase">Testo (modifica se necessario):</Label>
              <span className="text-xs text-muted-foreground">
                {voiceText.length} caratteri
              </span>
            </div>
            <Textarea
              value={voiceText}
              onChange={(e) => {
                setVoiceText(e.target.value);
                setVoiceParsedData(null); // Reset parsing quando si modifica
              }}
              placeholder="Parla o scrivi qui... Es: 'Appuntamento per Mario da OB Italia dopodomani alle 14:30'"
              className="rounded-none min-h-[100px] text-base"
              data-testid="voice-text-input"
            />
            <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
              <strong>Suggerimenti:</strong> Includi nome tecnico, cliente, data (domani/dopodomani/lunedì/15 marzo), ora (alle 10, ore 14:30)
            </div>
          </div>

          <div className="flex gap-2">
            {isListening ? (
              <Button onClick={onStop} className="flex-1 h-12 rounded-none bg-red-500 hover:bg-red-600">
                <MicOff className="h-5 w-5 mr-2" />
                Ferma Registrazione
              </Button>
            ) : (
              <Button onClick={onStart} variant="outline" className="flex-1 h-12 rounded-none border-cyan-500 text-cyan-600">
                <Mic className="h-5 w-5 mr-2" />
                {voiceText ? 'Aggiungi con Voce' : 'Inizia Registrazione'}
              </Button>
            )}

            {voiceText && !isListening && (
              <Button
                onClick={() => onParse(voiceText)}
                className="flex-1 h-12 rounded-none"
                disabled={voiceParsing || !voiceText.trim()}
              >
                {voiceParsing ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Analizzo...</>
                ) : (
                  'Analizza Testo'
                )}
              </Button>
            )}
          </div>

          {voiceParsedData && (
            <div className="space-y-4">
              <div className="p-4 border-2 border-cyan-500 bg-cyan-50 dark:bg-cyan-950 rounded">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  ✅ Appuntamento Riconosciuto:
                </h4>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Data:</span>
                    <p className="font-medium text-lg">{voiceParsedData.data}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ora:</span>
                    <p className="font-medium text-lg">{voiceParsedData.ora_inizio} - {voiceParsedData.ora_fine}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Tecnico:</span>
                    {voiceParsedData.tecnico ? (
                      <p className="font-medium flex items-center gap-2 text-lg">
                        <span
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: voiceParsedData.tecnico.colore || '#3b82f6' }}
                        ></span>
                        {voiceParsedData.tecnico.nome} {voiceParsedData.tecnico.cognome}
                      </p>
                    ) : (
                      <p className="text-amber-600">⚠️ Non riconosciuto - seleziona nel form</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Cliente:</span>
                    {voiceParsedData.cliente ? (
                      <p className="font-medium text-lg">{voiceParsedData.cliente.ragione_sociale}</p>
                    ) : (
                      <p className="text-amber-600">⚠️ Non trovato - cerca nel form</p>
                    )}
                  </div>
                  {voiceParsedData.luogo && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Luogo:</span>
                      <p className="font-medium">{voiceParsedData.luogo}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setVoiceParsedData(null)} className="flex-1 rounded-none">
                  Modifica Testo
                </Button>
                <Button onClick={onConfirm} className="flex-1 rounded-none bg-cyan-500 hover:bg-cyan-600">
                  Conferma → Apri Form
                </Button>
              </div>
            </div>
          )}

          <Button variant="ghost" onClick={onCancel} className="w-full rounded-none text-muted-foreground">
            Annulla
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
